/**
 * Accreditation Scopes — Full Governance Page
 * SuperAdmin: Define ISO scopes + manage CB scope assignments.
 * CertBody Admin: View their own approved scopes.
 */
import React, { useState, useEffect, useCallback } from 'react';
import axiosClient from '../api/axiosClient';

// ── Constants ─────────────────────────────────────────────────────────────────

const SCOPE_STATUS_FILTERS = ['all', 'pending', 'approved', 'suspended', 'revoked'];

const STATUS_COLORS = {
  pending:   { background: '#e9ecef', color: '#495057', border: '#dee2e6' },
  approved:  { background: '#d4edda', color: '#155724', border: '#c3e6cb' },
  suspended: { background: '#fff3cd', color: '#856404', border: '#ffeeba' },
  revoked:   { background: '#f8d7da', color: '#721c24', border: '#f5c6cb' },
};

const CATEGORY_COLORS = {
  'Quality':              '#17a2b8',
  'Environmental':        '#28a745',
  'Health & Safety':      '#fd7e14',
  'Information Security': '#6f42c1',
  'Food Safety':          '#e83e8c',
  'Medical Devices':      '#dc3545',
  'Energy':               '#ffc107',
  'Governance':           '#6c757d',
  'IT':                   '#007bff',
  'Risk':                 '#20c997',
  'Business Continuity':  '#343a40',
  'Privacy':              '#6f42c1',
};

// ── Small shared components ───────────────────────────────────────────────────

function StatusBadge({ status }) {
  const c = STATUS_COLORS[status] || STATUS_COLORS.pending;
  return (
    <span style={{
      display: 'inline-block', padding: '3px 10px', borderRadius: '12px',
      fontSize: '0.74rem', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.4px',
      background: c.background, color: c.color, border: `1px solid ${c.border}`,
    }}>
      {status}
    </span>
  );
}

function CategoryTag({ category }) {
  const color = CATEGORY_COLORS[category] || '#6c757d';
  return (
    <span style={{
      display: 'inline-block', padding: '2px 8px', borderRadius: '4px',
      fontSize: '0.72rem', fontWeight: '600',
      background: color + '22', color, border: `1px solid ${color}66`,
    }}>
      {category || 'General'}
    </span>
  );
}

function Btn({ label, onClick, variant = 'default', small = false, disabled = false }) {
  const v = {
    default: { bg: '#6c757d', fg: '#fff' },
    primary: { bg: '#d4af37', fg: '#1a1a1a' },
    success: { bg: '#28a745', fg: '#fff' },
    warning: { bg: '#fd7e14', fg: '#fff' },
    danger:  { bg: '#dc3545', fg: '#fff' },
    info:    { bg: '#17a2b8', fg: '#fff' },
  }[variant] || { bg: '#6c757d', fg: '#fff' };
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        padding: small ? '3px 8px' : '6px 14px',
        fontSize: small ? '0.72rem' : '0.82rem',
        fontWeight: '600', border: 'none', borderRadius: '4px',
        cursor: disabled ? 'not-allowed' : 'pointer',
        background: disabled ? '#ccc' : v.bg, color: disabled ? '#888' : v.fg,
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </button>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────

function AccreditationScopes() {
  const [userRole, setUserRole] = useState(null);

  // Scope definitions
  const [scopes, setScopes] = useState([]);
  const [loadingScopes, setLoadingScopes] = useState(false);
  const [scopeFilter, setScopeFilter] = useState('');        // category filter

  // CB Scope assignments
  const [assignments, setAssignments] = useState([]);
  const [loadingAssignments, setLoadingAssignments] = useState(false);
  const [assignFilter, setAssignFilter] = useState('all');   // status filter
  const [cbFilter, setCbFilter] = useState('');              // cert_body_id filter

  // Cert bodies (for assignment dropdown)
  const [certBodies, setCertBodies] = useState([]);

  // Banners
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // ── Modal state ────────────────────────────────────────────────────────────

  // Create scope
  const [showCreateScope, setShowCreateScope] = useState(false);
  const [scopeForm, setScopeForm] = useState({ code: '', name: '', description: '', category: '' });
  const [creatingSc, setCreatingSc] = useState(false);

  // Edit scope
  const [showEditScope, setShowEditScope] = useState(false);
  const [editScopeTarget, setEditScopeTarget] = useState(null);
  const [editScopeForm, setEditScopeForm] = useState({ name: '', description: '', category: '' });
  const [editingSc, setEditingSc] = useState(false);

  // Assign scope to CB
  const [showAssign, setShowAssign] = useState(false);
  const [assignForm, setAssignForm] = useState({ cert_body_id: '', scope_id: '', notes: '' });
  const [assigning, setAssigning] = useState(false);

  // Approve/Suspend/Revoke/Reinstate
  const [showAction, setShowAction] = useState(false);
  const [actionTarget, setActionTarget] = useState(null);
  const [actionType, setActionType] = useState('');
  const [actionNotes, setActionNotes] = useState('');
  const [performingAction, setPerformingAction] = useState(false);

  // Evidence upload
  const [showEvidence, setShowEvidence] = useState(false);
  const [evidenceTarget, setEvidenceTarget] = useState(null);
  const [evidenceFile, setEvidenceFile] = useState(null);
  const [uploadingEv, setUploadingEv] = useState(false);

  // ── Data fetching ──────────────────────────────────────────────────────────

  useEffect(() => {
    // Detect role from local storage / JWT
    try {
      const raw = localStorage.getItem('user') || sessionStorage.getItem('user');
      if (raw) setUserRole(JSON.parse(raw).role);
    } catch { /* ignore */ }
    fetchCertBodies();
  }, []);

  const fetchScopes = useCallback(async () => {
    setLoadingScopes(true);
    try {
      const params = scopeFilter ? { category: scopeFilter } : {};
      const res = await axiosClient.get('/scopes/', { params });
      setScopes(res.data || []);
    } catch { setError('Failed to load scope definitions.'); }
    finally { setLoadingScopes(false); }
  }, [scopeFilter]);

  const fetchAssignments = useCallback(async () => {
    setLoadingAssignments(true);
    try {
      const params = {};
      if (assignFilter !== 'all') params.status = assignFilter;
      if (cbFilter) params.cert_body_id = cbFilter;
      const res = await axiosClient.get('/cert-body-scopes/', { params });
      setAssignments(res.data || []);
    } catch { setError('Failed to load scope assignments.'); }
    finally { setLoadingAssignments(false); }
  }, [assignFilter, cbFilter]);

  const fetchCertBodies = async () => {
    try {
      const res = await axiosClient.get('/cert-bodies/');
      setCertBodies(res.data || []);
    } catch { /* non-critical */ }
  };

  useEffect(() => { fetchScopes(); }, [fetchScopes]);
  useEffect(() => { fetchAssignments(); }, [fetchAssignments]);

  const flash = (msg) => {
    setSuccess(msg); setError(null);
    setTimeout(() => setSuccess(null), 4000);
  };

  const isSuperAdmin = userRole === 'super_admin';

  // ── All unique categories ──────────────────────────────────────────────────
  const categories = [...new Set(scopes.map(s => s.category).filter(Boolean))].sort();

  // ── Handlers ──────────────────────────────────────────────────────────────

  const handleCreateScope = async () => {
    if (!scopeForm.code.trim() || !scopeForm.name.trim()) {
      setError('Code and name are required.'); return;
    }
    setCreatingSc(true);
    try {
      await axiosClient.post('/scopes/', {
        code: scopeForm.code.trim(),
        name: scopeForm.name.trim(),
        description: scopeForm.description.trim() || null,
        category: scopeForm.category.trim() || null,
      });
      setShowCreateScope(false);
      setScopeForm({ code: '', name: '', description: '', category: '' });
      fetchScopes();
      flash('Scope created successfully.');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create scope.');
    } finally { setCreatingSc(false); }
  };

  const openEditScope = (scope) => {
    setEditScopeTarget(scope);
    setEditScopeForm({ name: scope.name, description: scope.description || '', category: scope.category || '' });
    setShowEditScope(true);
  };

  const handleEditScope = async () => {
    setEditingSc(true);
    try {
      await axiosClient.patch(`/scopes/${editScopeTarget.id}`, {
        name: editScopeForm.name.trim(),
        description: editScopeForm.description.trim() || null,
        category: editScopeForm.category.trim() || null,
      });
      setShowEditScope(false);
      fetchScopes();
      flash('Scope updated.');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update scope.');
    } finally { setEditingSc(false); }
  };

  const handleAssign = async () => {
    if (!assignForm.cert_body_id || !assignForm.scope_id) {
      setError('Certification body and scope are both required.'); return;
    }
    setAssigning(true);
    try {
      await axiosClient.post('/cert-body-scopes/', {
        cert_body_id: parseInt(assignForm.cert_body_id),
        scope_id: parseInt(assignForm.scope_id),
        notes: assignForm.notes.trim() || null,
      });
      setShowAssign(false);
      setAssignForm({ cert_body_id: '', scope_id: '', notes: '' });
      fetchAssignments();
      flash('Scope assigned to certification body.');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to assign scope.');
    } finally { setAssigning(false); }
  };

  const openAction = (assignment, type) => {
    setActionTarget(assignment); setActionType(type); setActionNotes(''); setShowAction(true);
  };

  const handleAction = async () => {
    setPerformingAction(true);
    try {
      await axiosClient.patch(`/cert-body-scopes/${actionTarget.id}/${actionType}`, {
        notes: actionNotes.trim() || null,
      });
      setShowAction(false);
      fetchAssignments();
      flash(`Scope assignment ${actionType}d.`);
    } catch (err) {
      setError(err.response?.data?.detail || `Failed to ${actionType} scope assignment.`);
    } finally { setPerformingAction(false); }
  };

  const handleEvidenceUpload = async () => {
    if (!evidenceFile) { setError('Please select a file.'); return; }
    setUploadingEv(true);
    try {
      const fd = new FormData();
      fd.append('file', evidenceFile);
      await axiosClient.post(`/cert-body-scopes/${evidenceTarget.id}/upload-evidence`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setShowEvidence(false); setEvidenceFile(null);
      fetchAssignments();
      flash('Evidence uploaded.');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to upload evidence.');
    } finally { setUploadingEv(false); }
  };

  // ── Styles ─────────────────────────────────────────────────────────────────

  const s = {
    container:     { padding: '24px', maxWidth: '1400px', margin: '0 auto' },
    header:        { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '28px' },
    title:         { fontSize: '1.9rem', fontWeight: '700', color: '#1a1a1a', margin: 0 },
    subtitle:      { color: '#666', margin: '6px 0 0 0', fontSize: '0.95rem' },
    banner: t => ({
      padding: '10px 16px', borderRadius: '6px', marginBottom: '18px',
      fontWeight: '500', fontSize: '0.9rem',
      background: t === 'error' ? '#ffebee' : '#e8f5e9',
      border: `1px solid ${t === 'error' ? '#ef9a9a' : '#a5d6a7'}`,
      color: t === 'error' ? '#b71c1c' : '#1b5e20',
    }),
    section: {
      background: '#fff', border: '2px solid #d4af37',
      borderRadius: '8px', marginBottom: '32px', overflow: 'hidden',
    },
    sectionHead: {
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      padding: '14px 20px', background: '#1a1a1a', color: '#d4af37',
    },
    sectionTitle: { fontSize: '1rem', fontWeight: '700', margin: 0 },
    filterRow: {
      display: 'flex', gap: '8px', padding: '10px 18px',
      background: '#f9f5e7', borderBottom: '1px solid #e8ddb5',
      flexWrap: 'wrap', alignItems: 'center',
    },
    pill: active => ({
      padding: '4px 13px', borderRadius: '14px', fontSize: '0.78rem',
      fontWeight: '600', cursor: 'pointer', border: '2px solid',
      borderColor: active ? '#d4af37' : '#ccc',
      background: active ? '#d4af37' : '#fff',
      color: active ? '#1a1a1a' : '#666', textTransform: 'capitalize',
    }),
    table:   { width: '100%', borderCollapse: 'collapse' },
    th:      { padding: '10px 14px', textAlign: 'left', fontSize: '0.74rem', textTransform: 'uppercase', fontWeight: '700', color: '#666', borderBottom: '2px solid #e8ddb5', whiteSpace: 'nowrap' },
    td:      { padding: '10px 14px', fontSize: '0.87rem', color: '#333', borderBottom: '1px solid #f0f0f0', verticalAlign: 'middle' },
    ag:      { display: 'flex', gap: '5px', flexWrap: 'wrap' },
    btnSave: { padding: '8px 18px', background: '#d4af37', color: '#1a1a1a', border: 'none', borderRadius: '6px', fontWeight: '700', cursor: 'pointer' },
    btnCancel: { padding: '8px 16px', background: '#f1f1f1', border: '1px solid #ccc', borderRadius: '6px', fontWeight: '600', cursor: 'pointer' },
    empty:   { padding: '32px', textAlign: 'center', color: '#999', fontSize: '0.95rem' },
    overlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' },
    modal:   { background: '#fff', borderRadius: '10px', padding: '28px', width: '100%', maxWidth: '500px', maxHeight: '85vh', overflowY: 'auto', boxShadow: '0 8px 32px rgba(0,0,0,0.2)' },
    mtitle:  { fontSize: '1.15rem', fontWeight: '700', color: '#1a1a1a', marginBottom: '18px' },
    field:   { marginBottom: '13px' },
    label:   { display: 'block', fontWeight: '600', fontSize: '0.84rem', color: '#555', marginBottom: '5px' },
    input:   { width: '100%', padding: '8px 10px', fontSize: '0.9rem', border: '1.5px solid #ccc', borderRadius: '5px', boxSizing: 'border-box' },
    textarea:{ width: '100%', padding: '8px 10px', fontSize: '0.85rem', border: '1.5px solid #ccc', borderRadius: '5px', boxSizing: 'border-box', minHeight: '72px', resize: 'vertical' },
    select:  { width: '100%', padding: '8px 10px', fontSize: '0.9rem', border: '1.5px solid #ccc', borderRadius: '5px', boxSizing: 'border-box', background: '#fff' },
    mfooter: { display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '18px' },
  };

  // ── Render helpers ─────────────────────────────────────────────────────────

  const ACTION_CFG = {
    approve:   { label: 'Approve Scope',   color: '#28a745', desc: 'The CB will be authorised to issue certificates for this standard.' },
    suspend:   { label: 'Suspend Scope',   color: '#fd7e14', desc: 'The CB scope will be temporarily suspended.' },
    revoke:    { label: 'Revoke Scope',    color: '#dc3545', desc: 'The CB scope will be permanently revoked.' },
    reinstate: { label: 'Reinstate Scope', color: '#28a745', desc: 'The CB scope will be restored to approved status.' },
  };

  // ── JSX ────────────────────────────────────────────────────────────────────

  return (
    <div style={s.container}>
      {/* Header */}
      <div style={s.header}>
        <div>
          <h1 style={s.title}>Accreditation Scopes</h1>
          <p style={s.subtitle}>
            Define ISO standard scopes and manage which certification bodies are approved for each scope.
          </p>
        </div>
      </div>

      {/* Banners */}
      {error && (
        <div style={s.banner('error')}>
          {error}
          <button onClick={() => setError(null)} style={{ float: 'right', background: 'none', border: 'none', cursor: 'pointer', fontWeight: '700' }}>✕</button>
        </div>
      )}
      {success && <div style={s.banner('success')}>{success}</div>}

      {/* ── Section 1: Scope Catalogue ──────────────────────────────────────── */}
      <div style={s.section}>
        <div style={s.sectionHead}>
          <h2 style={s.sectionTitle}>Scope Catalogue ({scopes.length} scope{scopes.length !== 1 ? 's' : ''})</h2>
          {isSuperAdmin && (
            <Btn label="+ New Scope" variant="primary" onClick={() => setShowCreateScope(true)} />
          )}
        </div>

        {/* Category filter pills */}
        <div style={s.filterRow}>
          <span style={{ fontSize: '0.78rem', fontWeight: '600', color: '#888' }}>Category:</span>
          <button style={s.pill(!scopeFilter)} onClick={() => setScopeFilter('')}>All</button>
          {categories.map(cat => (
            <button key={cat} style={s.pill(scopeFilter === cat)} onClick={() => setScopeFilter(cat)}>
              {cat}
            </button>
          ))}
        </div>

        <div className="ds-table-scroll" style={{ overflowX: 'auto' }}>
          {loadingScopes ? (
            <div style={s.empty}>Loading scopes…</div>
          ) : scopes.length === 0 ? (
            <div style={s.empty}>No scopes defined yet.</div>
          ) : (
            <table style={s.table}>
              <thead>
                <tr>
                  {['Code', 'Name', 'Category', 'Description', ...(isSuperAdmin ? ['Actions'] : [])].map(h => (
                    <th key={h} style={s.th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {scopes.map(scope => (
                  <tr key={scope.id}>
                    <td style={{ ...s.td, fontWeight: '700', fontFamily: 'monospace', color: '#1a1a1a' }}>
                      {scope.code}
                    </td>
                    <td style={{ ...s.td, fontWeight: '600' }}>{scope.name}</td>
                    <td style={s.td}>
                      {scope.category ? <CategoryTag category={scope.category} /> : <span style={{ color: '#bbb' }}>—</span>}
                    </td>
                    <td style={{ ...s.td, color: '#666', maxWidth: '260px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {scope.description || '—'}
                    </td>
                    {isSuperAdmin && (
                      <td style={s.td}>
                        <div style={s.ag}>
                          <Btn label="Edit" variant="default" small onClick={() => openEditScope(scope)} />
                          <Btn label="Assign to CB" variant="primary" small onClick={() => {
                            setAssignForm(f => ({ ...f, scope_id: String(scope.id) }));
                            setShowAssign(true);
                          }} />
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* ── Section 2: CB Scope Assignments ────────────────────────────────── */}
      <div style={s.section}>
        <div style={s.sectionHead}>
          <h2 style={s.sectionTitle}>CB Scope Assignments ({assignments.length})</h2>
          {isSuperAdmin && (
            <Btn label="+ Assign Scope" variant="primary" onClick={() => setShowAssign(true)} />
          )}
        </div>

        {/* Status + CB filters */}
        <div style={s.filterRow}>
          <span style={{ fontSize: '0.78rem', fontWeight: '600', color: '#888' }}>Status:</span>
          {SCOPE_STATUS_FILTERS.map(f => (
            <button key={f} style={s.pill(assignFilter === f)} onClick={() => setAssignFilter(f)}>
              {f === 'all' ? 'All' : f}
            </button>
          ))}
          {isSuperAdmin && certBodies.length > 0 && (
            <>
              <span style={{ fontSize: '0.78rem', fontWeight: '600', color: '#888', marginLeft: '12px' }}>CB:</span>
              <select
                value={cbFilter}
                onChange={e => setCbFilter(e.target.value)}
                style={{ padding: '3px 8px', fontSize: '0.8rem', border: '1px solid #ccc', borderRadius: '4px', background: '#fff' }}
              >
                <option value="">All CBs</option>
                {certBodies.map(cb => (
                  <option key={cb.id} value={cb.id}>{cb.name}</option>
                ))}
              </select>
            </>
          )}
        </div>

        <div className="ds-table-scroll" style={{ overflowX: 'auto' }}>
          {loadingAssignments ? (
            <div style={s.empty}>Loading assignments…</div>
          ) : assignments.length === 0 ? (
            <div style={s.empty}>No scope assignments found.</div>
          ) : (
            <table style={s.table}>
              <thead>
                <tr>
                  {['ID', 'Certification Body', 'Scope Code', 'Scope Name', 'Category', 'Status', 'Evidence', 'Notes', 'Actions'].map(h => (
                    <th key={h} style={s.th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {assignments.map(a => (
                  <tr key={a.id}>
                    <td style={s.td}>{a.id}</td>
                    <td style={{ ...s.td, fontWeight: '600' }}>{a.cert_body_name || `CB #${a.cert_body_id}`}</td>
                    <td style={{ ...s.td, fontWeight: '700', fontFamily: 'monospace' }}>{a.scope_code}</td>
                    <td style={s.td}>{a.scope_name}</td>
                    <td style={s.td}>
                      {a.scope_category ? <CategoryTag category={a.scope_category} /> : '—'}
                    </td>
                    <td style={s.td}><StatusBadge status={a.status} /></td>
                    <td style={s.td}>
                      {a.evidence_url
                        ? <a href={a.evidence_url} target="_blank" rel="noreferrer" style={{ color: '#d4af37', fontWeight: '600' }}>View</a>
                        : <span style={{ color: '#bbb' }}>None</span>}
                    </td>
                    <td style={{ ...s.td, maxWidth: '140px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {a.notes || '—'}
                    </td>
                    <td style={s.td}>
                      {isSuperAdmin ? (
                        <div style={s.ag}>
                          {a.status === 'pending' && (
                            <>
                              <Btn label="Approve" variant="success" small onClick={() => openAction(a, 'approve')} />
                              <Btn label="Revoke"  variant="danger"  small onClick={() => openAction(a, 'revoke')} />
                            </>
                          )}
                          {a.status === 'approved' && (
                            <>
                              <Btn label="Suspend" variant="warning" small onClick={() => openAction(a, 'suspend')} />
                              <Btn label="Revoke"  variant="danger"  small onClick={() => openAction(a, 'revoke')} />
                            </>
                          )}
                          {a.status === 'suspended' && (
                            <>
                              <Btn label="Reinstate" variant="success" small onClick={() => openAction(a, 'reinstate')} />
                              <Btn label="Revoke"    variant="danger"  small onClick={() => openAction(a, 'revoke')} />
                            </>
                          )}
                          {a.status === 'revoked' && (
                            <Btn label="Reinstate" variant="success" small onClick={() => openAction(a, 'reinstate')} />
                          )}
                          <Btn label="Evidence" variant="info" small onClick={() => { setEvidenceTarget(a); setShowEvidence(true); }} />
                        </div>
                      ) : (
                        <span style={{ color: '#999', fontSize: '0.8rem' }}>Read-only</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* ── Create Scope Modal ─────────────────────────────────────────────── */}
      {showCreateScope && (
        <div style={s.overlay} onClick={() => setShowCreateScope(false)}>
          <div style={s.modal} onClick={e => e.stopPropagation()}>
            <div style={s.mtitle}>New Scope Definition</div>
            <div style={s.field}>
              <label style={s.label}>Code * <span style={{ fontWeight: '400', color: '#888' }}>(e.g. ISO9001)</span></label>
              <input style={s.input} value={scopeForm.code}
                onChange={e => setScopeForm(f => ({ ...f, code: e.target.value.toUpperCase().replace(/\s/g, '') }))}
                placeholder="ISO9001" />
            </div>
            <div style={s.field}>
              <label style={s.label}>Name *</label>
              <input style={s.input} value={scopeForm.name}
                onChange={e => setScopeForm(f => ({ ...f, name: e.target.value }))}
                placeholder="Quality Management Systems" />
            </div>
            <div style={s.field}>
              <label style={s.label}>Category</label>
              <input style={s.input} value={scopeForm.category}
                onChange={e => setScopeForm(f => ({ ...f, category: e.target.value }))}
                placeholder="Quality, Environmental, Information Security…" />
            </div>
            <div style={s.field}>
              <label style={s.label}>Description</label>
              <textarea style={s.textarea} value={scopeForm.description}
                onChange={e => setScopeForm(f => ({ ...f, description: e.target.value }))}
                placeholder="Brief description of this scope…" />
            </div>
            <div style={s.mfooter}>
              <button style={s.btnCancel} onClick={() => setShowCreateScope(false)}>Cancel</button>
              <button style={s.btnSave} onClick={handleCreateScope} disabled={creatingSc}>
                {creatingSc ? 'Creating…' : 'Create Scope'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Edit Scope Modal ───────────────────────────────────────────────── */}
      {showEditScope && editScopeTarget && (
        <div style={s.overlay} onClick={() => setShowEditScope(false)}>
          <div style={s.modal} onClick={e => e.stopPropagation()}>
            <div style={s.mtitle}>Edit Scope — <code>{editScopeTarget.code}</code></div>
            <div style={s.field}>
              <label style={s.label}>Name *</label>
              <input style={s.input} value={editScopeForm.name}
                onChange={e => setEditScopeForm(f => ({ ...f, name: e.target.value }))} />
            </div>
            <div style={s.field}>
              <label style={s.label}>Category</label>
              <input style={s.input} value={editScopeForm.category}
                onChange={e => setEditScopeForm(f => ({ ...f, category: e.target.value }))} />
            </div>
            <div style={s.field}>
              <label style={s.label}>Description</label>
              <textarea style={s.textarea} value={editScopeForm.description}
                onChange={e => setEditScopeForm(f => ({ ...f, description: e.target.value }))} />
            </div>
            <div style={s.mfooter}>
              <button style={s.btnCancel} onClick={() => setShowEditScope(false)}>Cancel</button>
              <button style={s.btnSave} onClick={handleEditScope} disabled={editingSc}>
                {editingSc ? 'Saving…' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Assign Scope Modal ─────────────────────────────────────────────── */}
      {showAssign && (
        <div style={s.overlay} onClick={() => setShowAssign(false)}>
          <div style={s.modal} onClick={e => e.stopPropagation()}>
            <div style={s.mtitle}>Assign Scope to Certification Body</div>
            <div style={s.field}>
              <label style={s.label}>Certification Body *</label>
              <select style={s.select} value={assignForm.cert_body_id}
                onChange={e => setAssignForm(f => ({ ...f, cert_body_id: e.target.value }))}>
                <option value="">— Select Certification Body —</option>
                {certBodies.map(cb => (
                  <option key={cb.id} value={cb.id}>{cb.name}</option>
                ))}
              </select>
            </div>
            <div style={s.field}>
              <label style={s.label}>Scope *</label>
              <select style={s.select} value={assignForm.scope_id}
                onChange={e => setAssignForm(f => ({ ...f, scope_id: e.target.value }))}>
                <option value="">— Select Scope —</option>
                {scopes.map(sc => (
                  <option key={sc.id} value={sc.id}>{sc.code} — {sc.name}</option>
                ))}
              </select>
            </div>
            <div style={s.field}>
              <label style={s.label}>Notes</label>
              <textarea style={s.textarea} value={assignForm.notes}
                onChange={e => setAssignForm(f => ({ ...f, notes: e.target.value }))}
                placeholder="Optional notes about this assignment…" />
            </div>
            <div style={s.mfooter}>
              <button style={s.btnCancel} onClick={() => setShowAssign(false)}>Cancel</button>
              <button style={s.btnSave} onClick={handleAssign} disabled={assigning}>
                {assigning ? 'Assigning…' : 'Assign Scope'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Action Modal ───────────────────────────────────────────────────── */}
      {showAction && actionTarget && (() => {
        const cfg = ACTION_CFG[actionType] || {};
        return (
          <div style={s.overlay} onClick={() => setShowAction(false)}>
            <div style={s.modal} onClick={e => e.stopPropagation()}>
              <div style={s.mtitle}>{cfg.label}</div>
              <p style={{ fontSize: '0.87rem', color: '#555', marginBottom: '12px' }}>{cfg.desc}</p>
              <p style={{ fontSize: '0.85rem', color: '#444', marginBottom: '14px' }}>
                <strong>CB:</strong> {actionTarget.cert_body_name} &nbsp;|&nbsp;
                <strong>Scope:</strong> {actionTarget.scope_code}
              </p>
              <div style={s.field}>
                <label style={s.label}>Notes (optional)</label>
                <textarea style={s.textarea} value={actionNotes}
                  onChange={e => setActionNotes(e.target.value)}
                  placeholder="Reason for this action…" />
              </div>
              <div style={s.mfooter}>
                <button style={s.btnCancel} onClick={() => setShowAction(false)}>Cancel</button>
                <button
                  style={{ ...s.btnSave, background: cfg.color, color: '#fff' }}
                  onClick={handleAction} disabled={performingAction}
                >
                  {performingAction ? 'Processing…' : cfg.label}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Evidence Upload Modal ──────────────────────────────────────────── */}
      {showEvidence && evidenceTarget && (
        <div style={s.overlay} onClick={() => setShowEvidence(false)}>
          <div style={s.modal} onClick={e => e.stopPropagation()}>
            <div style={s.mtitle}>Upload Evidence — Assignment #{evidenceTarget.id}</div>
            <p style={{ fontSize: '0.85rem', color: '#666', marginBottom: '12px' }}>
              <strong>{evidenceTarget.cert_body_name}</strong> / <code>{evidenceTarget.scope_code}</code>
            </p>
            <div style={s.field}>
              <label style={s.label}>Select File (PDF, PNG, JPG, DOC/DOCX — max 10 MB)</label>
              <input type="file" accept=".pdf,.png,.jpg,.jpeg,.doc,.docx"
                onChange={e => setEvidenceFile(e.target.files[0])} />
            </div>
            {evidenceTarget.evidence_url && (
              <p style={{ fontSize: '0.82rem', color: '#666' }}>
                Current: <a href={evidenceTarget.evidence_url} target="_blank" rel="noreferrer" style={{ color: '#d4af37' }}>View existing evidence</a>
              </p>
            )}
            <div style={s.mfooter}>
              <button style={s.btnCancel} onClick={() => { setShowEvidence(false); setEvidenceFile(null); }}>Cancel</button>
              <button style={s.btnSave} onClick={handleEvidenceUpload} disabled={uploadingEv || !evidenceFile}>
                {uploadingEv ? 'Uploading…' : 'Upload Evidence'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AccreditationScopes;
